import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { CheckCircle, PaintBrush, Lightning, Trophy } from "@phosphor-icons/react";

const SPECIALTIES = [
  "Logo & branding", "Business cards", "Menus", "Packaging", "Brochures",
  "Social media", "Illustration", "Typography", "Photo retouch", "3D / product",
];

const setSEO = (title, desc) => {
  document.title = title;
  const q = (sel) => document.querySelector(sel);
  q('meta[name="description"]')?.setAttribute("content", desc);
  q('meta[property="og:title"]')?.setAttribute("content", title);
  q('meta[property="og:description"]')?.setAttribute("content", desc);
};

export default function Designers() {
  const [f, setF] = useState({
    name: "", email: "", phone: "", city: "", specialties: [],
    hourly_rate: "", years_experience: "", portfolio_urls: "", bio: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setSEO(
      "Become a Designer — Talaio/AI",
      "Join Talaio/AI as a freelance designer. AI generates the concepts, you do the polish. Real brand briefs, pre-qualified clients, weekly payouts. Apply now."
    );
  }, []);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (s) => setF({ ...f, specialties: f.specialties.includes(s) ? f.specialties.filter(x => x !== s) : [...f.specialties, s] });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/apply/designer", {
        ...f,
        hourly_rate: f.hourly_rate ? Number(f.hourly_rate) : null,
        years_experience: f.years_experience ? Number(f.years_experience) : null,
        portfolio_urls: f.portfolio_urls.split(/[\n,]+/).map(s => s.trim()).filter(Boolean),
      });
      setSubmitted(true);
      toast.success("Application received. We'll be in touch within 48 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Application failed");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--pf-bg)]">
        <Nav />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-500 mb-6" />
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4" data-testid="designer-application-success">Application received.</h1>
          <p className="text-lg text-[var(--pf-text-2)] mb-8">
            Thanks {f.name}. We review portfolios within 48 hours. Watch <strong>{f.email}</strong> for our reply.
          </p>
          <Link to="/" className="pf-btn-secondary inline-block">← Back to homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="pf-label mb-4 inline-flex items-center gap-2"><PaintBrush size={14} /> For Designers</div>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-5xl">
            Get paid to <br className="hidden sm:block"/>polish AI concepts.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-[var(--pf-text-2)] max-w-2xl">
            AI generates 3 print-ready concepts per brief. You refine typography, fix logos, deliver the vector file. Real brands, real budgets, real payouts.
          </p>
          <a href="#apply" data-testid="designer-hero-cta" className="pf-btn-primary mt-8 inline-flex">Apply to join →</a>
        </motion.div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 grid md:grid-cols-3 gap-6">
        {[
          { icon: Lightning, title: "AI does the heavy lifting", desc: "Every brief comes with 3 AI-generated concepts. You skip the blank canvas and go straight to refinement." },
          { icon: Trophy, title: "Quality-scored briefs", desc: "Top-rated designers get first pick of premium human-designer briefs (typically ₹5k–₹50k projects)." },
          { icon: CheckCircle, title: "Weekly payouts", desc: "Complete a project, get paid the next Friday. No 60-day agency payment cycles." },
        ].map(({ icon: Icon, title, desc }, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="border border-[var(--pf-border)] bg-white p-6">
            <Icon size={28} className="text-[var(--pf-brand)] mb-3" />
            <h3 className="font-black text-xl tracking-tight mb-2">{title}</h3>
            <p className="text-sm text-[var(--pf-text-2)] leading-relaxed">{desc}</p>
          </motion.div>
        ))}
      </section>

      <section id="apply" className="max-w-3xl mx-auto px-6 md:px-12 py-16 border-t border-[var(--pf-border)]">
        <div className="pf-label mb-3">Apply</div>
        <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-3">Show us your work.</h2>
        <p className="text-[var(--pf-text-2)] mb-10">Portfolio links matter more than a resume. Share your best 3–5 samples.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="designer-apply-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="pf-label block mb-2">Full name *</label>
              <input required data-testid="designer-name" className="pf-input" value={f.name} onChange={upd("name")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Email *</label>
              <input required type="email" data-testid="designer-email" className="pf-input" value={f.email} onChange={upd("email")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Phone / WhatsApp</label>
              <input data-testid="designer-phone" placeholder="+91 …" className="pf-input" value={f.phone} onChange={upd("phone")} />
            </div>
            <div>
              <label className="pf-label block mb-2">City</label>
              <input data-testid="designer-city" className="pf-input" value={f.city} onChange={upd("city")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Hourly rate (₹, optional)</label>
              <input type="number" data-testid="designer-rate" className="pf-input" value={f.hourly_rate} onChange={upd("hourly_rate")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Years of experience</label>
              <input type="number" data-testid="designer-years" className="pf-input" value={f.years_experience} onChange={upd("years_experience")} />
            </div>
          </div>

          <div>
            <label className="pf-label block mb-2">Specialties</label>
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES.map(s => (
                <button type="button" key={s} onClick={() => toggle(s)}
                  data-testid={`designer-specialty-${s.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}
                  className={`text-xs uppercase tracking-widest font-bold px-3 py-2 border-2 transition-colors ${
                    f.specialties.includes(s) ? "bg-[var(--pf-text)] text-white border-[var(--pf-text)]" : "bg-white border-[var(--pf-border)] hover:border-[var(--pf-text)]"
                  }`}>{s}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="pf-label block mb-2">Portfolio links * (Behance, Dribbble, Instagram, personal site — one per line)</label>
            <textarea required rows={4} data-testid="designer-portfolio" className="pf-input resize-none font-mono text-sm" placeholder="https://behance.net/yourhandle&#10;https://dribbble.com/yourhandle" value={f.portfolio_urls} onChange={upd("portfolio_urls")} />
          </div>
          <div>
            <label className="pf-label block mb-2">Short bio (optional)</label>
            <textarea rows={3} data-testid="designer-bio" className="pf-input resize-none" value={f.bio} onChange={upd("bio")} />
          </div>

          <button type="submit" disabled={loading} data-testid="designer-submit" className="pf-btn-primary w-full sm:w-auto mt-4 justify-center">
            {loading ? "Sending…" : "Submit application →"}
          </button>
        </form>
      </section>
    </div>
  );
}
